import React, { useState, useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, FlatList, Modal, Pressable } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { PAYMENT_DESIGN_TOKENS, PAYMENT_TYPOGRAPHY, PAYMENT_SPACING } from '@/constants/residents/mobile/paymentDesignTokens';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { SVG_COMPONENTS } from '@/assets/admin';
import { MobilePagination } from '@/components/common/residents/mobile/MobilePagination';
import { formatCurrency, formatShortDate } from '@/utils/common/formatting';
import { mobilePaymentsStrings } from '@/constants/residents/strings';
import { getStatusLabel, getStatusTextColor, renderStatusIcon } from '@/features/payments/utils/paymentStatusUtils';
import type { HistoryRow, SortField, SortOrder } from '@/features/payments/hooks/useLeaseHistory';

const ChevronDown = RESIDENTS_SVG_COMPONENTS.CHEVRON_DOWN;
const CloseIcon = SVG_COMPONENTS.CANCEL_X;

// Sort option type
type SortOption = {
  id: string;
  label: string;
  field: SortField;
  order: SortOrder;
};

// Sort options matching Figma design
const SORT_OPTIONS: SortOption[] = [
  { id: 'description', label: 'Description', field: 'description', order: 'asc' },
  { id: 'posted_first', label: 'Posted first', field: 'date', order: 'desc' },
  { id: 'posted_last', label: 'Posted last', field: 'date', order: 'asc' },
  { id: 'largest_amount', label: 'Largest amount', field: 'amount', order: 'desc' },
  { id: 'lowest_amount', label: 'Lowest amount', field: 'amount', order: 'asc' },
];

interface MobileTransactionHistoryTableProps {
  transactions: HistoryRow[];
  isLoading?: boolean;
  pageSize?: number;
  /** Callback when a transaction row is pressed (for showing details modal) */
  onTransactionPress?: (transaction: HistoryRow) => void;
}

/**
 * Mobile Transaction History Table
 * Matches Figma design with two-column table format and sort bottom sheet
 */
export const MobileTransactionHistoryTable: React.FC<MobileTransactionHistoryTableProps> = ({
  transactions,
  isLoading = false,
  pageSize = 10,
  onTransactionPress,
}) => {
  const insets = useSafeAreaInsets();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedSortId, setSelectedSortId] = useState<string>('posted_first');
  const [showSortModal, setShowSortModal] = useState(false);

  // Get current sort config
  const currentSort = SORT_OPTIONS.find(opt => opt.id === selectedSortId) || SORT_OPTIONS[1];

  // Sort transactions
  const sortedTransactions = useMemo(() => {
    return [...transactions].sort((a, b) => {
      let aVal: any = a[currentSort.field];
      let bVal: any = b[currentSort.field];
      
      if (currentSort.field === 'amount') {
        aVal = Math.abs(Number(aVal));
        bVal = Math.abs(Number(bVal));
        return currentSort.order === 'asc' ? aVal - bVal : bVal - aVal;
      } else if (currentSort.field === 'date') {
        aVal = new Date(aVal).getTime() || 0;
        bVal = new Date(bVal).getTime() || 0;
        return currentSort.order === 'asc' ? aVal - bVal : bVal - aVal;
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
        const comparison = aVal.localeCompare(bVal);
        return currentSort.order === 'asc' ? comparison : -comparison;
      }
    });
  }, [transactions, currentSort]);

  // Paginate transactions
  const totalPages = Math.ceil(sortedTransactions.length / pageSize);
  const paginatedTransactions = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedTransactions.slice(startIndex, startIndex + pageSize);
  }, [sortedTransactions, currentPage, pageSize]);

  const handleSortSelect = (sortId: string) => {
    setSelectedSortId(sortId);
    setShowSortModal(false);
    setCurrentPage(1);
  };

  // Check if row is a late fee - ALL fields should be red
  const isLateFee = (row: HistoryRow): boolean => {
    return row.description.toLowerCase().includes('late fee');
  };

  // Check if row is a payment - amount should be green
  const isPayment = (row: HistoryRow): boolean => {
    // Check description starts with "Payment" OR type is 'payment' OR amount is negative (credit)
    return (
      row.description.toLowerCase().startsWith('payment') ||
      row.type === 'payment' ||
      row.amount < 0
    );
  };

  // Get amount color based on transaction type
  // Late fees: RED | Payments: GREEN | Charges: WHITE
  const getAmountColor = (row: HistoryRow): string => {
    if (isLateFee(row)) {
      return PAYMENT_DESIGN_TOKENS.TABLE_AMOUNT_NEGATIVE; // Red for late fees
    }
    if (isPayment(row)) {
      return PAYMENT_DESIGN_TOKENS.TABLE_AMOUNT_POSITIVE; // Green for payments
    }
    return PAYMENT_DESIGN_TOKENS.TABLE_AMOUNT_NEUTRAL; // White for charges
  };

  // Format amount display
  // Payments/credits shown with parentheses like ($250.00)
  const formatAmount = (row: HistoryRow): string => {
    const amount = Math.abs(row.amount);
    // Show parentheses for payments with negative amounts (credits)
    if (row.amount < 0) {
      return `(${formatCurrency(amount)})`;
    }
    return formatCurrency(amount);
  };

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <Text style={styles.emptyStateText}>
        {mobilePaymentsStrings.emptyTransactionHistory}
      </Text>
    </View>
  );

  // Transaction row layout matching Figma - with status for payments
  const renderTransactionRow = ({ item }: { item: HistoryRow }) => {
    const lateFeeStyle = isLateFee(item);
    const hasStatus = item.type === 'payment' && item.status;
    const isClickable = hasStatus && onTransactionPress;
    
    const RowWrapper = isClickable ? TouchableOpacity : View;
    const rowWrapperProps = isClickable ? { onPress: () => onTransactionPress(item) } : {};
    
    return (
      <RowWrapper style={styles.tableRow} {...rowWrapperProps}>
        {/* Top Row: Date on left, Status on right (for payments) */}
        <View style={styles.dateStatusRow}>
          <Text style={styles.dateText}>
            {formatShortDate(item.date)}
          </Text>
          {hasStatus && (
            <View style={styles.statusContainer}>
              <Text style={[styles.statusText, { color: getStatusTextColor(item.status) }]}>
                {getStatusLabel(item.status)}
              </Text>
              {renderStatusIcon(item.status)}
            </View>
          )}
        </View>

        {/* Description */}
        <View style={styles.descriptionContainer}>
          <Text 
            style={[styles.descriptionText, lateFeeStyle && styles.valueRed]}
            numberOfLines={2}
          >
            {item.description}
          </Text>
        </View>

        {/* Amount */}
        <View style={styles.amountContainer}>
          <Text style={[styles.amountText, { color: getAmountColor(item) }]}>
            {formatAmount(item)}
          </Text>
        </View>
      </RowWrapper>
    );
  };

  // Sort Bottom Sheet Modal
  const renderSortModal = () => (
    <Modal
      visible={showSortModal}
      animationType="fade"
      transparent={true}
      onRequestClose={() => setShowSortModal(false)}
    >
      {/* Overlay */}
      <Pressable style={styles.modalOverlay} onPress={() => setShowSortModal(false)}>
        <View />
      </Pressable>

      {/* Bottom Sheet */}
      <View style={[styles.sortBottomSheet, { paddingBottom: Math.max(insets.bottom, 40) }]}>
        {/* Header */}
        <View style={styles.sortHeader}>
          <Text style={styles.sortHeaderTitle}>{mobilePaymentsStrings.sortBy}</Text>
          <TouchableOpacity
            style={styles.sortCloseButton}
            onPress={() => setShowSortModal(false)}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            {CloseIcon && <CloseIcon width={16} height={16} color={PAYMENT_DESIGN_TOKENS.TEXT_MUTED} />}
          </TouchableOpacity>
        </View>

        {/* Sort Options List */}
        <View style={styles.sortList}>
          {SORT_OPTIONS.map((option) => {
            const isSelected = option.id === selectedSortId;
            return (
              <TouchableOpacity
                key={option.id}
                style={styles.sortOption}
                onPress={() => handleSortSelect(option.id)}
              >
                {isSelected ? (
                  <View style={styles.sortOptionSelected}>
                    <View style={styles.sortOptionBullet} />
                    <Text style={styles.sortOptionTextSelected}>{option.label}</Text>
                  </View>
                ) : (
                  <Text style={styles.sortOptionText}>{option.label}</Text>
                )}
              </TouchableOpacity>
            );
          })}
        </View>
      </View>
    </Modal>
  );

  if (transactions.length === 0 && !isLoading) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>{mobilePaymentsStrings.transactionHistory}</Text>
        </View>
        {renderEmptyState()}
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header with sort */}
      <View style={styles.header}>
        <Text style={styles.headerTitle}>{mobilePaymentsStrings.transactionHistory}</Text>
        
        <TouchableOpacity
          style={styles.sortButton}
          onPress={() => setShowSortModal(true)}
          accessibilityLabel="Sort transactions"
          accessibilityRole="button"
        >
          <Text style={styles.sortButtonText}>{mobilePaymentsStrings.sortBy}</Text>
          <ChevronDown
            width={20}
            height={20}
            color={PAYMENT_DESIGN_TOKENS.TEXT_MUTED}
          />
        </TouchableOpacity>
      </View>

      {/* Transaction Rows */}
      <FlatList
        data={paginatedTransactions}
        renderItem={renderTransactionRow}
        keyExtractor={(item) => item.id}
        scrollEnabled={false}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <MobilePagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Sort Modal */}
      {renderSortModal()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: PAYMENT_SPACING.xl,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: PAYMENT_SPACING.sm,
  },
  headerTitle: {
    ...PAYMENT_TYPOGRAPHY.headingLg,
    color: PAYMENT_DESIGN_TOKENS.TEXT_PRIMARY,
  },
  sortButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PAYMENT_SPACING.xs,
  },
  sortButtonText: {
    ...PAYMENT_TYPOGRAPHY.textXsLight,
    color: PAYMENT_DESIGN_TOKENS.TEXT_MUTED,
  },
  // Table row styles - Figma: Mobile / Table item / List
  tableRow: {
    paddingTop: PAYMENT_SPACING.lg,
    borderTopWidth: 1,
    borderTopColor: PAYMENT_DESIGN_TOKENS.BORDER,
    gap: PAYMENT_SPACING.xs,
  },
  dateStatusRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: PAYMENT_SPACING.xs,
  },
  dateText: {
    ...PAYMENT_TYPOGRAPHY.textXs,
    color: PAYMENT_DESIGN_TOKENS.TEXT_SECONDARY, // #ACACAC
  },
  statusContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PAYMENT_SPACING.sm,
  },
  statusText: {
    ...PAYMENT_TYPOGRAPHY.textXsLight,
    color: PAYMENT_DESIGN_TOKENS.TEXT_PRIMARY,
  },
  descriptionContainer: {
    marginBottom: PAYMENT_SPACING.xs,
  },
  descriptionText: {
    ...PAYMENT_TYPOGRAPHY.bodySm,
    color: PAYMENT_DESIGN_TOKENS.TEXT_PRIMARY,
  },
  amountContainer: {
    // No extra margin needed
  },
  amountText: {
    ...PAYMENT_TYPOGRAPHY.bodySmSemiBold,
  },
  valueRed: {
    color: PAYMENT_DESIGN_TOKENS.TABLE_AMOUNT_NEGATIVE,
  },
  // Legacy styles kept for backward compatibility
  tableRowItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: PAYMENT_SPACING.sm,
  },
  labelColumn: {
    width: 106,
    ...PAYMENT_TYPOGRAPHY.textXsMedium,
    color: PAYMENT_DESIGN_TOKENS.TEXT_MUTED,
  },
  valueColumn: {
    flex: 1,
    ...PAYMENT_TYPOGRAPHY.bodySm,
    color: PAYMENT_DESIGN_TOKENS.TEXT_PRIMARY,
  },
  valueAmount: {
    flex: 1,
    ...PAYMENT_TYPOGRAPHY.bodySmSemiBold,
  },
  emptyState: {
    paddingVertical: PAYMENT_SPACING.xxl,
  },
  emptyStateText: {
    ...PAYMENT_TYPOGRAPHY.bodySm,
    color: PAYMENT_DESIGN_TOKENS.TEXT_MUTED,
    lineHeight: 21,
  },
  // Sort Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: PAYMENT_DESIGN_TOKENS.OVERLAY_BACKGROUND,
  },
  sortBottomSheet: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: PAYMENT_DESIGN_TOKENS.CARD_BACKGROUND_SECONDARY, // #242424
    borderTopLeftRadius: 32,
    borderTopRightRadius: 32,
    paddingTop: 24,
    paddingHorizontal: 24,
  },
  sortHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: PAYMENT_SPACING.lg,
  },
  sortHeaderTitle: {
    ...PAYMENT_TYPOGRAPHY.headingLg,
    color: PAYMENT_DESIGN_TOKENS.TEXT_PRIMARY,
    flex: 1,
  },
  sortCloseButton: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sortList: {
    gap: 0,
  },
  sortOption: {
    height: 40,
    justifyContent: 'center',
  },
  sortOptionText: {
    ...PAYMENT_TYPOGRAPHY.bodySm,
    color: '#C8C8C8', // gray/200 from Figma
  },
  sortOptionSelected: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  sortOptionBullet: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: PAYMENT_DESIGN_TOKENS.TEXT_PRIMARY,
  },
  sortOptionTextSelected: {
    ...PAYMENT_TYPOGRAPHY.bodySmMedium,
    color: PAYMENT_DESIGN_TOKENS.TEXT_PRIMARY,
  },
});
